import { css, styled } from 'styled-components';

import { theme } from '../../styles';
import { ChevronIconOld } from '../../assets/icons';
import { useTabbing } from '../../utils';
import { Accordion } from '../Accordion';
import { Animate } from '../Animate';
import { Link } from '../Link';
import { Text } from '../Text';
import { FAQCentered } from './FAQCentered';
import { FAQStretchedContainer } from './FAQFullWidth';
import { FAQModalContainer } from './FAQModal';
import { FAQSideContainer } from './FAQSide';
import { FAQDrawerContainer } from './FAQDrawer';
import type { QuestionsAndAnswersType } from './types';

type FAQProps = {
  /** Refers as a test id to the `data-testid` attribute of the FAQ */
  testId?: string;
  /** The type of variantions to display the FAQ */
  layout: 'fullWidth' | 'side' | 'modal' | 'centered' | 'drawer';
  /** List of questions and answers to show */
  questionAnswers: QuestionsAndAnswersType[];
};

const FAQHeader = styled.button<{ isTabbing: boolean; isEService: boolean }>`
  position: relative;
  width: 100%;
  text-align: left;
  border: 1px solid transparent; /* fix for button's default outline */
  border-top: 1px solid ${theme.colors.lightGrey};
  padding: ${props => (props.isEService ? '0.625rem 3.5rem 0.625rem 2.5rem' : '1.125rem 3.875rem 1.125rem 0.75rem')};
  background-color: transparent;
  user-select: none;
  appearance: none;
  cursor: pointer;
  outline: none;
  margin: 0px;

  :hover {
    background-color: ${props => (props.isEService ? theme.colors.black05 : theme.colors.white)};
  }

  :focus {
    ${props =>
      props.isTabbing &&
      css`
        outline: ${theme.colors.darkBlue} solid 2px;
        outline-offset: -2px;
      `}
  }

  @media screen and (max-width: ${theme.breakpoint.lg - 1}px) {
    padding-left: 1.25rem;
  }

  @media screen and (max-width: ${theme.breakpoint.md - 1}px) {
    padding: 1.75rem 3.875rem 1.75rem 0.625rem;
  }
`;

const FAQBody = styled.div<{ layout: string }>`
  padding: ${props =>
    props.layout === 'centered'
      ? '0.625rem 0.75rem 5rem 0.75rem'
      : props.layout === 'side'
        ? '0.25rem 3.5rem 2.5rem 2.5rem'
        : '0.25rem 3.875rem 2.5rem 0.75rem'};

  @media screen and (max-width: ${theme.breakpoint.lg - 1}px) {
    padding-left: 1.25rem;
    ${props => props.layout === 'centered' && 'padding-bottom: 3.75rem'};
  }

  @media screen and (max-width: ${theme.breakpoint.md - 1}px) {
    padding-left: 0.625rem;
    ${props => props.layout === 'centered' && 'padding-bottom: 3.125rem'}
  }
`;

const ChevronIconOldWrapper = styled.div`
  position: absolute;
  right: 1.5625rem;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  @media screen and (max-width: ${theme.breakpoint.md - 1}px) {
    cursor: pointer;
  }
`;

export const FAQ = ({ testId, layout, questionAnswers = [] }: FAQProps) => {
  const isEService = layout === 'side';
  const textSize = layout === 'centered' ? 'medium' : isEService ? 'small' : 'standard';
  const isTabbing = useTabbing();

  const renderFaq = () => (
    <Accordion>
      {questionAnswers.map(({ question, answer, linkUrl }, index: number) => (
        <Accordion.Item key={index}>
          <Accordion.Header
            renderHeader={(onToggle: any, isExpanded: boolean) => (
              <FAQHeader
                onClick={onToggle}
                isEService={isEService}
                isTabbing={isTabbing}
                data-testid={testId ? `${testId}-question-${index}` : undefined}
              >
                <Text renderAs="span" size={textSize} fontWeight="semibold">
                  {question}
                </Text>
                <ChevronIconOldWrapper>
                  <Animate.Rotate isTriggered={isExpanded} degrees={180}>
                    <ChevronIconOld
                      variant={isEService ? 'thin' : 'fat'}
                      direction="downward"
                      width={isEService ? 9 : 12}
                    />
                  </Animate.Rotate>
                </ChevronIconOldWrapper>
              </FAQHeader>
            )}
          />
          <Accordion.Body
            renderBody={(isExpanded: boolean) => (
              <Animate.Height isTriggered={isExpanded}>
                <FAQBody layout={layout}>
                  {typeof answer === 'string' ? (
                    <Text
                      marginBottom={linkUrl ? 20 : 0}
                      size={textSize}
                      testId={testId ? `${testId}-answer-${index}` : undefined}
                      whiteSpace="pre-line"
                    >
                      {answer}
                    </Text>
                  ) : (
                    answer
                  )}
                  {!!linkUrl && (
                    <Link to={linkUrl} fontSize="small" testId="faq-read-more-link">
                      Läs mer
                    </Link>
                  )}
                </FAQBody>
              </Animate.Height>
            )}
          />
        </Accordion.Item>
      ))}
    </Accordion>
  );
  return layout === 'fullWidth' ? (
    <FAQStretchedContainer>{renderFaq()}</FAQStretchedContainer>
  ) : layout === 'side' ? (
    <FAQSideContainer>{renderFaq()}</FAQSideContainer>
  ) : layout === 'modal' ? (
    <FAQModalContainer>{renderFaq()}</FAQModalContainer>
  ) : layout === 'drawer' ? (
    <FAQDrawerContainer>{renderFaq()}</FAQDrawerContainer>
  ) : (
    // layout === 'centered'
    <FAQCentered>{renderFaq()}</FAQCentered>
  );
};
